<template>
  <div class="ad-container" :class="containerClass">
    <div 
      ref="adContainer" 
      class="ad-placeholder"
      :class="placeholderClass"
    >
      <div v-if="!isLoaded" class="ad-loading">
        <div class="loading-spinner"></div>
        <span class="loading-text">{{ $t('ads.loading') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, inject, nextTick } from 'vue'

export default {
  name: 'AdSenseAd',
  props: {
    adSlotId: {
      type: String,
      required: true
    },
    adFormat: {
      type: String,
      default: 'auto'
    },
    adStyle: {
      type: String,
      default: 'display:block'
    },
    containerClass: {
      type: String,
      default: ''
    },
    placeholderClass: {
      type: String,
      default: ''
    },
    lazyLoad: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const adContainer = ref(null)
    const isLoaded = ref(false)
    const adSense = inject('adSense')
    let observer = null

    // Función para esperar el consentimiento del CMP - COMENTADA (iubenda deshabilitado)
    // const waitForCMPConsent = () => {
    //   return new Promise((resolve) => {
    //     // Si no hay CMP, continuar inmediatamente
    //     if (!window._iub || !window._iub.cs) {
    //       resolve(true)
    //       return
    //     }

    //     // Verificar si el CMP está listo
    //     const checkConsent = () => {
    //       if (window._iub && window._iub.cs && window._iub.cs.api) {
    //         window._iub.cs.api.getConsent().then((consent) => {
    //           if (consent && consent.consent) {
    //             console.log('Consentimiento del CMP obtenido')
    //             resolve(true)
    //           } else {
    //             console.log('Esperando consentimiento del CMP...')
    //             // Esperar un poco más
    //             setTimeout(checkConsent, 500)
    //           }
    //         }).catch(() => {
    //           // Si falla, continuar de todas formas
    //           console.warn('No se pudo verificar el consentimiento, continuando...')
    //           resolve(true)
    //         })
    //       } else {
    //         // Si el CMP no está listo, esperar un poco
    //         setTimeout(() => {
    //           if (window._iub && window._iub.cs && window._iub.cs.api) {
    //             checkConsent()
    //           } else {
    //             // Si después de 3 segundos no está listo, continuar
    //             console.warn('CMP no está listo después de esperar, continuando...')
    //             resolve(true)
    //           }
    //         }, 100)
    //       }
    //     }

    //     // Timeout de 5 segundos
    //     setTimeout(() => {
    //       console.warn('Timeout esperando consentimiento del CMP, continuando...')
    //       resolve(true)
    //     }, 5000)

    //     checkConsent()
    //   })
    // }

    const loadAd = async () => {
      if (!adSense || !adContainer.value) return

      try {
        // Esperar a que el DOM esté completamente renderizado
        await nextTick()
        
        // Esperar múltiples frames para asegurar que el layout esté completo
        await new Promise(resolve => requestAnimationFrame(resolve))
        await new Promise(resolve => requestAnimationFrame(resolve))
        
        // Esperar el consentimiento del CMP si está disponible - COMENTADO (iubenda deshabilitado)
        // await waitForCMPConsent()
        
        // Verificar que el contenedor tenga dimensiones antes de continuar
        const container = adContainer.value
        if (container) {
          const rect = container.getBoundingClientRect()
          const style = window.getComputedStyle(container)
          
          // Si no tiene dimensiones, esperar un poco más
          if (rect.width === 0 || rect.height === 0 || style.display === 'none') {
            console.warn('Contenedor sin dimensiones, esperando...')
            await new Promise(resolve => setTimeout(resolve, 300))
            
            // Verificar nuevamente
            const newRect = container.getBoundingClientRect()
            if (newRect.width === 0 || newRect.height === 0) {
              console.error('El contenedor aún no tiene dimensiones después de esperar')
              return
            }
          }
        }
        
        // Esperar a que AdSense esté cargado
        if (!adSense.isAdSenseAvailable()) {
          await adSense.loadAdSense(adSense.clientId)
          // Esperar un poco más para asegurar que esté completamente inicializado
          await new Promise(resolve => setTimeout(resolve, 200))
        }

        // Verificar nuevamente si AdSense está disponible después de intentar cargarlo
        if (!adSense.isAdSenseAvailable()) {
          console.warn('AdSense no está disponible. Los anuncios no se mostrarán.')
          isLoaded.value = true // Ocultar el loader
          return
        }

        // Insertar el anuncio (ahora es async)
        const adElement = await adSense.insertAd(
          adContainer.value,
          props.adSlotId,
          props.adFormat,
          props.adStyle
        )
        
        // Si no se pudo crear el elemento del anuncio, ocultar el loader
        if (!adElement) {
          console.warn('No se pudo crear el elemento del anuncio')
          isLoaded.value = true
          return
        }
        
        if (adElement) {
          // Esperar un poco más para verificar si el anuncio se cargó
          setTimeout(() => {
            // Verificar si el anuncio tiene contenido
            const hasContent = adElement.children.length > 0 || 
                              adElement.innerHTML.trim().length > 0 ||
                              adElement.dataset.adsbygoogleStatus === 'done'
            
            if (hasContent) {
              isLoaded.value = true
            } else {
              // Si no hay contenido después de 2 segundos, ocultar el loader de todas formas
              setTimeout(() => {
                isLoaded.value = true
              }, 2000)
            }
          }, 500)
        } else {
          isLoaded.value = true
        }
      } catch (error) {
        console.error('Error al cargar anuncio:', error)
      }
    }

    const setupIntersectionObserver = () => {
      if (!props.lazyLoad || !adContainer.value) return

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              loadAd()
              observer.unobserve(entry.target)
            }
          })
        },
        {
          rootMargin: '50px'
        }
      )

      observer.observe(adContainer.value)
    }

    onMounted(async () => {
      // Esperar a que el componente esté completamente montado
      await nextTick()
      
      if (props.lazyLoad) {
        setupIntersectionObserver()
      } else {
        // Delay adicional para asegurar que el layout esté completo
        // Esperar a que la página esté completamente cargada
        if (document.readyState === 'complete') {
          setTimeout(() => {
            loadAd()
          }, 200)
        } else {
          window.addEventListener('load', () => {
            setTimeout(() => {
              loadAd()
            }, 200)
          }, { once: true })
        }
      }
    })

    onUnmounted(() => {
      if (observer) {
        observer.disconnect()
      }
    })

    return {
      adContainer,
      isLoaded
    }
  }
}
</script>

<style scoped>
.ad-container {
  width: 100%;
  margin: 1rem 0;
}

.ad-placeholder {
  width: 100%;
  min-width: 320px;
  min-height: 90px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  display: block;
  position: relative;
  box-sizing: border-box;
  overflow: visible;
}

.ad-placeholder .adsbygoogle {
  display: block !important;
  width: 100% !important;
  min-height: 90px !important;
  position: relative !important;
  z-index: 1 !important;
}

.ad-placeholder .adsbygoogle iframe {
  display: block !important;
  width: 100% !important;
  min-height: 90px !important;
  border: none !important;
  margin: 0 !important;
  padding: 0 !important;
}


.dark .ad-placeholder {
  background: #2d3748;
  border-color: #4a5568;
}

.ad-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  z-index: 10;
  pointer-events: none;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e2e8f0;
  border-top: 2px solid #3182ce;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.dark .loading-spinner {
  border-color: #4a5568;
  border-top-color: #63b3ed;
}

.loading-text {
  font-size: 0.875rem;
  color: #6b7280;
}

.dark .loading-text {
  color: #a0aec0;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Estilos específicos para diferentes formatos */
.ad-placeholder.banner {
  min-height: 90px;
}

.ad-placeholder.sidebar {
  min-height: 600px;
}

.ad-placeholder.in-article {
  min-height: 250px;
}

.ad-placeholder.in-feed {
  min-height: 200px;
}
</style> 